import { Household, Meeting, MeetingConsentAttestedStatus, MeetingType, TranscriptSpeaker } from "@/schema"
import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"

import {
  attestMeetingConsent,
  createInstantMeeting,
  createMeetingNotetaker,
  createMeetingTranscript,
  createRecordingTranscription,
  deleteMeeting,
  deleteMeetingNotetaker,
  findAndReplace,
  FindAndReplaceParams,
  generateReviewMaterials,
  MeetingsListResponse,
  savePrepAsNotes,
  sendLiveBot,
  updateMeeting,
  updateMeetingPrivacy,
  updateMeetingWithFiles,
  updateTranscriptSpeaker,
} from "./api"

function patchHouseholdSchedule(household: Household, householdIds: string[], meeting: Meeting): Household {
  if (!householdIds.includes(household.id)) return household

  const updatedHousehold = meeting.households?.find(({ id }) => id === household.id)
  if (!updatedHousehold) return household

  return {
    ...household,
    updated_at: updatedHousehold.updated_at,
    last_meeting_id: updatedHousehold.last_meeting_id,
    last_meeting_at: updatedHousehold.last_meeting_at,
    next_meeting_id: updatedHousehold.next_meeting_id,
    next_meeting_at: updatedHousehold.next_meeting_at,
    last_review_meeting_id: updatedHousehold.last_review_meeting_id,
    last_review_meeting_at: updatedHousehold.last_review_meeting_at,
    next_review_meeting_id: updatedHousehold.next_review_meeting_id,
    next_review_meeting_at: updatedHousehold.next_review_meeting_at,
    next_review_due_on: updatedHousehold.next_review_due_on,
    next_review_due_on_override: updatedHousehold.next_review_due_on_override,
  }
}

function patchHouseholdCollections(data: unknown, householdIds: string[], meeting: Meeting): unknown {
  if (!data || typeof data !== "object") return data

  if ("pages" in data && Array.isArray(data.pages)) {
    return {
      ...data,
      pages: data.pages.map((page) => patchHouseholdCollections(page, householdIds, meeting)),
    }
  }

  if ("clients" in data && Array.isArray(data.clients)) {
    return {
      ...data,
      clients: data.clients.map((client) => patchHouseholdSchedule(client as Household, householdIds, meeting)),
    }
  }

  if ("prospects" in data && Array.isArray(data.prospects)) {
    return {
      ...data,
      prospects: data.prospects.map((prospect) => patchHouseholdSchedule(prospect as Household, householdIds, meeting)),
    }
  }

  return data
}

const CONTENT_ONLY_UPDATE_KEYS = new Set(["notepad", "ai_summary"])

// Autosaves are not serialized, so an older response can arrive after a newer one. Every save
// touches the meeting's updated_at, which makes it a reliable ordering key for the cache.
function isAtLeastAsNewAsCached(cached: Meeting | undefined, updated: Meeting): boolean {
  if (!cached?.updated_at || !updated.updated_at) return true
  return new Date(updated.updated_at).getTime() >= new Date(cached.updated_at).getTime()
}

export function useUpdateMeeting() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      meetingId,
      data,
    }: {
      meetingId: string
      data: Partial<Meeting> & {
        household_ids?: string[]
        contact_ids?: string[]
        business_ids?: string[]
        user_ids?: string[]
        meeting_topic_ids?: string[]
      }
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateMeeting(context, meetingId, data)
    },
    onMutate: async ({ meetingId, data }) => {
      await queryClient.cancelQueries({ queryKey: ["meeting", meetingId] })

      const previousMeeting = queryClient.getQueryData<Meeting>(["meeting", meetingId])

      if (previousMeeting) {
        queryClient.setQueryData<Meeting>(["meeting", meetingId], {
          ...previousMeeting,
          ...data,
        })
      }

      return { previousMeeting }
    },
    onError: (_err, { meetingId }, context) => {
      if (context?.previousMeeting) {
        queryClient.setQueryData(["meeting", meetingId], context.previousMeeting)
      }
    },
    onSuccess: (updatedMeeting, { meetingId, data }) => {
      if (isAtLeastAsNewAsCached(queryClient.getQueryData<Meeting>(["meeting", meetingId]), updatedMeeting)) {
        queryClient.setQueryData<Meeting>(["meeting", meetingId], updatedMeeting)
      }
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
      if (data.meeting_topic_ids !== undefined) {
        queryClient.invalidateQueries({ queryKey: ["meeting-topics"] })
      }
      if (Object.keys(data).every((key) => CONTENT_ONLY_UPDATE_KEYS.has(key))) return

      queryClient.invalidateQueries({ queryKey: ["meeting"] })
      queryClient.invalidateQueries({ queryKey: ["household"] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] })
    },
  })
}

export function useUpdateMeetingPrivacy() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ meetingId, excludedUserIds }: { meetingId: string; excludedUserIds: string[] }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateMeetingPrivacy(context, meetingId, excludedUserIds)
    },
    meta: { errorToast: { fallback: "Failed to update meeting privacy" } },
    onSuccess: (privacy, { meetingId }) => {
      queryClient.setQueryData(["meetingPrivacy", meetingId], privacy)
      queryClient.invalidateQueries({ queryKey: ["meeting", meetingId] })
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
      queryClient.invalidateQueries({ queryKey: ["household"] })
    },
  })
}

export function useAttestMeetingConsent() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      meetingId,
      attestedStatus,
    }: {
      meetingId: string
      attestedStatus: MeetingConsentAttestedStatus
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return attestMeetingConsent(context, meetingId, attestedStatus)
    },
    onSuccess: (_consent, { meetingId }) => {
      queryClient.invalidateQueries({ queryKey: ["meeting", meetingId] })
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })
}

export function useUpdateMeetingInTable() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      meetingId,
      data,
    }: {
      meetingId: string
      data: Omit<Partial<Meeting>, "households"> & {
        household_ids?: string[]
        contact_ids?: string[]
        business_ids?: string[]
        households?: { id: string; name: string; type: "Client" | "Prospect" | "PastClient" }[]
      }
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { households, ...apiData } = data
      return updateMeeting(context, meetingId, apiData)
    },
    onMutate: async ({ meetingId, data }) => {
      await queryClient.cancelQueries({ queryKey: ["meetings", "infinite"] })

      const queryCache = queryClient.getQueryCache()
      const previousData: Record<string, InfiniteData<MeetingsListResponse> | undefined> = {}

      queryCache.getAll().forEach((query) => {
        if (query.queryKey[0] === "meetings" && query.queryKey[1] === "infinite") {
          const key = JSON.stringify(query.queryKey)
          previousData[key] = query.state.data as InfiniteData<MeetingsListResponse> | undefined

          queryClient.setQueryData<InfiniteData<MeetingsListResponse>>(query.queryKey, (old) => {
            if (!old) return old

            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                meetings: page.meetings.map((meeting) =>
                  meeting.id === meetingId ? ({ ...meeting, ...data } as Meeting) : meeting
                ),
              })),
            }
          })
        }
      })

      return { previousData }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        Object.entries(context.previousData).forEach(([key, data]) => {
          if (data) {
            const queryKey = JSON.parse(key)
            queryClient.setQueryData(queryKey, data)
          }
        })
      }
    },
  })
}

export function useUpdateMeetingWithFiles() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      files,
      existingSignedIds = [],
    }: {
      id: string
      files: File[]
      existingSignedIds?: string[]
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateMeetingWithFiles(context, id, files, existingSignedIds)
    },
    meta: { errorToast: { fallback: "Failed to upload file" } },
    onSuccess: (updatedMeeting) => {
      queryClient.setQueryData<Meeting>(["meeting", updatedMeeting.id], updatedMeeting)
      queryClient.invalidateQueries({ queryKey: ["meeting"] })
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })
}

export function useDeleteMeeting() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (meetingId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return deleteMeeting(context, meetingId)
    },
    meta: { errorToast: { fallback: "Failed to delete meeting" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })
}

export function useCreateInstantMeeting() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      householdId,
      data,
    }: {
      householdId: string
      data: { meeting_link?: string; title?: string }
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createInstantMeeting(context, householdId, data)
    },
    meta: { errorToast: { fallback: "Failed to create meeting" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })
}

interface CreateInstantMeetingParams {
  data: {
    meeting_link?: string
    title?: string
    household_ids?: string[]
    contact_ids?: string[]
    business_ids?: string[]
    meeting_type?: MeetingType
  }
  overrideBookId?: string
}

export function useCreateInstantMeetingWithoutHousehold() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ data, overrideBookId }: CreateInstantMeetingParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return apiContextFetch<Meeting>(context, `meetings`, {
        method: "POST",
        body: JSON.stringify({
          meeting: {
            ...data,
            meeting_source: "instant",
          },
          override_book_id: overrideBookId,
        }),
      })
    },
    meta: { errorToast: { fallback: "Failed to create meeting" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })
}

interface CreateLoggedMeetingParams {
  data: {
    start_time: string
    household_ids?: string[]
    contact_ids?: string[]
    business_ids?: string[]
    title?: string
    meeting_link?: string
    meeting_type?: MeetingType
  }
  overrideBookId?: string
}

export function useCreateLoggedMeeting() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  const patchHouseholdSchedules = (meeting: Meeting, householdIds: string[]) => {
    householdIds.forEach((householdId) => {
      queryClient.setQueryData<Household>(["household", householdId], (household) =>
        household ? patchHouseholdSchedule(household, householdIds, meeting) : household
      )
      queryClient.setQueryData<Household>(["client", householdId], (household) =>
        household ? patchHouseholdSchedule(household, householdIds, meeting) : household
      )
      queryClient.setQueryData<Household>(["prospect", householdId], (household) =>
        household ? patchHouseholdSchedule(household, householdIds, meeting) : household
      )
    })
    queryClient.setQueriesData({ queryKey: ["clients"] }, (data) =>
      patchHouseholdCollections(data, householdIds, meeting)
    )
    queryClient.setQueriesData({ queryKey: ["prospects"] }, (data) =>
      patchHouseholdCollections(data, householdIds, meeting)
    )
  }

  return useMutation({
    mutationFn: async ({ data, overrideBookId }: CreateLoggedMeetingParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return apiContextFetch<Meeting>(context, `meetings`, {
        method: "POST",
        body: JSON.stringify({
          meeting: {
            ...data,
            meeting_source: "logged",
          },
          override_book_id: overrideBookId,
        }),
      })
    },
    meta: { errorToast: { fallback: "Failed to log meeting" } },
    onSuccess: (meeting, { data }) => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] })

      const householdIds = data.household_ids ?? []
      const isPastClientReview =
        data.meeting_type === "client_review" && new Date(data.start_time).getTime() <= Date.now()

      if (!isPastClientReview || householdIds.length === 0) {
        queryClient.invalidateQueries({ queryKey: ["household"] })
        queryClient.invalidateQueries({ queryKey: ["clients"] })
        queryClient.invalidateQueries({ queryKey: ["prospects"] })
        return
      }

      patchHouseholdSchedules(meeting, householdIds)
      queryClient.invalidateQueries({ queryKey: ["household"], refetchType: "none" })
      queryClient.invalidateQueries({ queryKey: ["clients"], refetchType: "none" })
      queryClient.invalidateQueries({ queryKey: ["prospects"], refetchType: "none" })
    },
  })
}

export function useToggleMeetingNotetakerMutation(meetingId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  const createMutation = useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createMeetingNotetaker(context, meetingId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] })
      queryClient.invalidateQueries({ queryKey: ["meeting"] })
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return deleteMeetingNotetaker(context, meetingId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] })
      queryClient.invalidateQueries({ queryKey: ["meeting"] })
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })

  return {
    createNotetaker: createMutation.mutate,
    createNotetakerAsync: createMutation.mutateAsync,
    createLoading: createMutation.isPending,
    deleteNotetaker: deleteMutation.mutate,
    deleteNotetakerAsync: deleteMutation.mutateAsync,
    deleteLoading: deleteMutation.isPending,
    isLoading: createMutation.isPending || deleteMutation.isPending,
  }
}

export function useGenerateReviewMaterials() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ meetingId, force }: { meetingId: string; force?: boolean }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return generateReviewMaterials(context, meetingId, { force })
    },
    onSuccess: (data, { meetingId }) => {
      queryClient.setQueryData<Meeting>(["meeting", meetingId], data)
      queryClient.invalidateQueries({ queryKey: ["meeting"] })
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })
}

export function useCreateRecordingTranscription() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      meetingId,
      speakers,
      additionalInstructions,
    }: {
      meetingId: string
      speakers?: string[]
      additionalInstructions?: string
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createRecordingTranscription(context, meetingId, { speakers, additionalInstructions })
    },
    meta: { errorToast: { fallback: "Failed to transcribe the recording" } },
    onSuccess: ({ recording_status }, { meetingId }) => {
      queryClient.setQueryData<Meeting>(["meeting", meetingId], (meeting) =>
        meeting
          ? {
              ...meeting,
              recording_retranscription_available: false,
              multiple_speaker_retranscription_available: false,
              recording_reprocessing_failed: false,
              is_processing_recording: true,
              primary_recording: meeting.primary_recording
                ? { ...meeting.primary_recording, status: recording_status }
                : meeting.primary_recording,
            }
          : meeting
      )
      queryClient.invalidateQueries({ queryKey: ["meeting", meetingId] })
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })
}

export function useSendLiveBot() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (meetingId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return sendLiveBot(context, meetingId)
    },
    meta: { errorToast: { fallback: "Failed to send the bot" } },
    onSuccess: (data, meetingId) => {
      queryClient.setQueryData<Meeting>(["meeting", meetingId], data)
      queryClient.invalidateQueries({ queryKey: ["meeting"] })
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })
}

export function useCreateMeetingTranscript() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ meetingId, text }: { meetingId: string; text: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createMeetingTranscript(context, meetingId, { user_upload_text: text })
    },
    meta: { errorToast: { fallback: "Failed to upload transcript" } },
    onSuccess: (_data, { meetingId }) => {
      queryClient.invalidateQueries({ queryKey: ["meeting", meetingId] })
      queryClient.invalidateQueries({ queryKey: ["meetings"] })
    },
  })
}

export function useSavePrepAsNotes() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (meetingId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return savePrepAsNotes(context, meetingId)
    },
    meta: { errorToast: { fallback: "Failed to save meeting prep as notes" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notes"] })
    },
  })
}

export function useRenameTranscriptSpeaker() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      meetingId,
      speakerId,
      overriddenName,
    }: {
      meetingId: string
      speakerId: string
      overriddenName: string | null
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateTranscriptSpeaker(context, meetingId, speakerId, {
        overridden_name: overriddenName,
      })
    },
    onMutate: async ({ meetingId, speakerId, overriddenName }) => {
      await queryClient.cancelQueries({ queryKey: ["meeting", meetingId] })

      const previousMeeting = queryClient.getQueryData<Meeting>(["meeting", meetingId])

      if (previousMeeting?.transcript_speakers) {
        queryClient.setQueryData<Meeting>(["meeting", meetingId], {
          ...previousMeeting,
          transcript_speakers: previousMeeting.transcript_speakers.map((s: TranscriptSpeaker) =>
            s.id === speakerId
              ? {
                  ...s,
                  overridden_name: overriddenName,
                  effective_name: overriddenName || s.original_name,
                }
              : s
          ),
        })
      }

      return { previousMeeting }
    },
    onError: (_err, { meetingId }, context) => {
      if (context?.previousMeeting) {
        queryClient.setQueryData(["meeting", meetingId], context.previousMeeting)
      }
    },
    meta: { errorToast: { fallback: "Failed to rename speaker" } },
  })
}

export function useFindAndReplace() {
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async ({ meetingId, data }: { meetingId: string; data: FindAndReplaceParams }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return findAndReplace(context, meetingId, data)
    },
    meta: { errorToast: { fallback: "Failed to find and replace" } },
  })
}
